/**
 * Hook de embarques: carga, crea y elimina embarques.
 * Recuerda el embarque seleccionado en localStorage para persistir entre recargas.
 */
import { useState, useCallback } from 'react';
import { storage } from '@/services/storage';
import { embarkationsService, type Embarkation } from '@/features/embarkations/services/embarkationsService';

const SELECTED_EMBARKATION_STORAGE_KEY = 'selectedEmbarkationId';

export function useEmbarkations() {
    const [embarkations, setEmbarkations] = useState<Embarkation[]>([]);

    const fetchEmbarkations = async (): Promise<Embarkation[]> => {
        try {
            const list = await embarkationsService.getAll();
            setEmbarkations(list);
            return list;
        } catch (error) {
            console.error('Error fetching embarkations:', error);
            return [];
        }
    };

    const addEmbarkation = async (nombre: string): Promise<Embarkation | null> => {
        try {
            const row = await embarkationsService.create(nombre);
            setEmbarkations(prev => [...prev, row]);
            return row;
        } catch (error) {
            console.error('Error adding embarkation:', error);
            return null;
        }
    };

    const deleteEmbarkation = async (id: number): Promise<boolean> => {
        try {
            await embarkationsService.remove(id);
            setEmbarkations(prev => prev.filter(e => e.id !== id));
            return true;
        } catch (error) {
            console.error('Error deleting embarkation:', error);
            return false;
        }
    };

    const updateEmbarkationInList = useCallback((id: number, patch: Partial<Embarkation>) => {
        setEmbarkations(prev => prev.map(e => (e.id === id ? { ...e, ...patch } : e)));
    }, []);

    const getInitialEmbarkation = (list: Embarkation[]): Embarkation | null => {
        if (list.length === 0) return null;
        const savedId = storage.getItem(SELECTED_EMBARKATION_STORAGE_KEY);
        if (savedId) {
            const saved = list.find(e => e.id.toString() === savedId);
            if (saved) return saved;
        }
        return list[0];
    };

    return {
        embarkations,
        fetchEmbarkations,
        addEmbarkation,
        deleteEmbarkation,
        updateEmbarkationInList,
        getInitialEmbarkation,
        selectedEmbarkationStorageKey: SELECTED_EMBARKATION_STORAGE_KEY,
    };
}
